import React, { useState, useEffect, useCallback } from 'react';
import { inspectionsApi, buildingsApi } from '../services/api';
import type { LifecycleState, PipelineCountsResponse } from '../services/api';
import { Icon, IconName } from '../ds/components/Icon';
import { ProgressBar } from '../ds/components/ProgressBar';
import { Button } from '../ds/components/Button';
import { STATE_ORDER, LIFECYCLE_META, stageCount } from './lifecycleMeta';
import type { TabType } from '../types';

interface DashboardStats {
  totalInspections: number;
  scheduledInspections: number;
  inProgressInspections: number;
  completedInspections: number;
}

interface DashboardViewProps {
  /**
   * Optional drill-down: clicking a stat tile navigates to the matching tab.
   * Wire `setActiveTab` from App.tsx to enable; omitted = tiles are non-interactive.
   * Keeping it optional preserves the existing `<DashboardView />` call site.
   */
  onNavigate?: (tab: TabType) => void;
  /**
   * Drill-down do „Budynki" z prefiltrem etapu (#10): klik kafla lifecycle
   * przekazuje stan, klik „Budynki"/„czeka na CEEB" — null (grupa Aktywne).
   */
  onNavigateToStage?: (stage: LifecycleState | null) => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({ onNavigate, onNavigateToStage }) => {
  const [stats, setStats] = useState<DashboardStats>({
    totalInspections: 0,
    scheduledInspections: 0,
    inProgressInspections: 0,
    completedInspections: 0,
  });
  // Liczniki lifecycle z GET /buildings/pipeline/counts — jedno źródło prawdy o budynkach (#10).
  const [pipeline, setPipeline] = useState<PipelineCountsResponse | null>(null);
  // Migr. 073: kafle etapów liczą jak chipy listy budynków — bez budynków „W kolejce” (te nie są w etapach listy;
  // klik w kafel otwiera listę, która ich nie pokazuje). `counts` z backendu = wszystkie budynki per stan.
  const stageCounts = pipeline?.counts
    ? (Object.fromEntries(Object.entries(pipeline.counts).map(([st, n]) => [
      st, Math.max(0, (n || 0) - (pipeline.queued_counts?.[st as LifecycleState] || 0)),
    ])) as Record<LifecycleState, number>)
    : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // pipelineCounts zamiast buildingsApi.getAll() — Pulpit nie potrzebuje 3771
      // pełnych budynków z lokalami, tylko liczników per stan (perf + model lifecycle).
      const [countsRes, inspections] = await Promise.all([
        buildingsApi.pipelineCounts(),
        inspectionsApi.getAll(),
      ]);

      const scheduledInspections = inspections.filter(
        (i) => i.status === 'PLANNED'
      ).length;
      const inProgressInspections = inspections.filter(
        (i) => i.status === 'IN_PROGRESS' || i.status === 'READY_FOR_CEEB'
      ).length;
      const completedInspections = inspections.filter(
        (i) => i.status === 'COMPLETED'
      ).length;

      setPipeline(countsRes);
      setStats({
        totalInspections: inspections.length,
        scheduledInspections,
        inProgressInspections,
        completedInspections,
      });
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Nie udało się wczytać statystyk. Sprawdź połączenie i spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 'var(--space-4)',
          color: 'var(--text-secondary)',
        }}
      >
        <Icon
          name="refresh"
          size={48}
          color="var(--action-primary)"
          style={{ animation: 'ds-spin 1s linear infinite' }}
        />
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)' }}>
          Ładowanie statystyk...
        </span>
        <style>{`@keyframes ds-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Flat stat tiles: white card + colored left 6px bar accent + DS Icon (no gradients).
  const statCards: Array<{
    value: React.ReactNode;
    label: string;
    icon: IconName;
    bar: string;
    iconColor: string;
    iconBg: string;
    tab?: TabType;
    stage?: LifecycleState | null;
    testId?: string;
  }> = [
    {
      value: pipeline?.total ?? 0,
      label: 'Budynki',
      icon: 'building',
      bar: 'var(--action-primary)',
      iconColor: 'var(--action-primary)',
      iconBg: 'var(--changed-bg)',
      stage: null,
    },
    {
      value: stats.totalInspections,
      label: 'Przeglądy',
      icon: 'clipboard',
      bar: 'var(--status-rec-bar)',
      iconColor: 'var(--status-rec)',
      iconBg: 'var(--status-rec-bg)',
      // issue #9: „Przeglądy" prowadzi do „Budynki" w toku — dawniej osobna zakładka
      // inspections-in-progress (już nie istnieje). Pkt 22: etap scalony „Zaplanowany".
      stage: 'ZAPLANOWANY',
    },
    {
      value: pipeline?.pending_ceeb ?? 0,
      label: 'Budynki z lokalami czekającymi na CEEB',
      icon: 'upload',
      bar: 'var(--status-rec-bar)',
      iconColor: 'var(--status-rec)',
      iconBg: 'var(--status-rec-bg)',
      stage: null,
      testId: 'dashboard-pending-ceeb',
    },
  ];

  return (
    <div style={{ background: 'var(--surface-page)', minHeight: '100%' }}>
      {/* Header + refresh */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1
            style={{
              margin: '0 0 4px',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-h1)',
              fontWeight: 'var(--weight-bold)',
              color: 'var(--text-heading)',
            }}
          >
            Pulpit
          </h1>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-body)',
              color: 'var(--text-secondary)',
            }}
          >
            Przegląd systemu kontroli przewodów kominowych
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          {lastUpdated && (
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-caption)',
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
              }}
            >
              Aktualizacja: {lastUpdated.toLocaleTimeString('pl-PL')}
            </span>
          )}
          <Button variant="ghost" size="md" icon="refresh" onClick={loadStats}>
            Odśwież
          </Button>
        </div>
      </div>

      {/* Error banner (DS deny tint) */}
      {error && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-4) var(--space-5)',
            marginBottom: 'var(--space-6)',
            background: 'var(--status-deny-bg)',
            border: 'var(--border-width) solid var(--status-deny-bar)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--status-deny)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
          }}
        >
          <Icon name="triangle-alert" size={28} style={{ flex: 'none' }} />
          <span style={{ flex: 1 }}>{error}</span>
          <Button variant="secondary" size="md" icon="refresh" onClick={loadStats}>
            Spróbuj ponownie
          </Button>
        </div>
      )}

      {/* Main stats cards — flat white + left 6px bar */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
        }}
      >
        {statCards.map((card, idx) => {
          const cardStage = card.stage;
          const activate =
            cardStage !== undefined && onNavigateToStage
              ? () => onNavigateToStage(cardStage)
              : card.tab && onNavigate
              ? () => onNavigate(card.tab!)
              : undefined;
          const interactive = !!activate;
          return (
            <div
              key={idx}
              data-testid={card.testId}
              onClick={activate}
              role={interactive ? 'button' : undefined}
              tabIndex={interactive ? 0 : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        activate!();
                      }
                    }
                  : undefined
              }
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-5)',
                paddingLeft: 'calc(var(--space-5) + var(--bar-width))',
                background: 'var(--surface-card)',
                border: 'var(--border-width) solid var(--border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                cursor: interactive ? 'pointer' : 'default',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 'var(--bar-width)',
                  background: card.bar,
                }}
              />
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 56,
                  height: 56,
                  borderRadius: 'var(--radius-md)',
                  flex: 'none',
                  background: card.iconBg,
                  color: card.iconColor,
                }}
              >
                <Icon name={card.icon} size={30} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-display)',
                    fontWeight: 'var(--weight-bold)',
                    lineHeight: 'var(--leading-tight)',
                    color: 'var(--text-heading)',
                    fontVariantNumeric: 'tabular-nums',
                    wordBreak: 'break-word',
                  }}
                >
                  {card.value}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-label)',
                    color: 'var(--text-secondary)',
                    marginTop: 6,
                  }}
                >
                  {card.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bars section — inspection statuses */}
      <section
        style={{
          padding: 'var(--space-5)',
          marginBottom: 'var(--space-6)',
          background: 'var(--surface-card)',
          border: 'var(--border-width) solid var(--border)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <h2
          style={{
            margin: '0 0 var(--space-5)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-h2)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--text-heading)',
          }}
        >
          Status przeglądów
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 'var(--space-5)',
          }}
        >
          <ProgressBar
            label="Zaplanowane"
            value={stats.scheduledInspections}
            max={stats.totalInspections}
            showCount={false}
          />
          <ProgressBar
            label="W trakcie"
            value={stats.inProgressInspections}
            max={stats.totalInspections}
            showCount={false}
          />
          <ProgressBar
            label="Ukończone"
            value={stats.completedInspections}
            max={stats.totalInspections}
            showCount={false}
          />
        </div>
        {/* Explicit counts (ProgressBar showCount renders "lokale"; these are inspections) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 'var(--space-5)',
            marginTop: 'var(--space-3)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-label)',
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--text-secondary)',
          }}
        >
          <span><strong style={{ color: 'var(--text-primary)', fontWeight: 'var(--weight-semibold)' }}>{stats.scheduledInspections}</strong> zaplanowanych</span>
          <span><strong style={{ color: 'var(--text-primary)', fontWeight: 'var(--weight-semibold)' }}>{stats.inProgressInspections}</strong> w trakcie</span>
          <span><strong style={{ color: 'var(--text-primary)', fontWeight: 'var(--weight-semibold)' }}>{stats.completedInspections}</strong> ukończonych</span>
        </div>
      </section>

      {/* Budynki wg etapu lifecycle (#10) — kafle z pipeline/counts, klik = Budynki z prefiltrem */}
      <section
        style={{
          padding: 'var(--space-5)',
          marginBottom: 'var(--space-6)',
          background: 'var(--surface-card)',
          border: 'var(--border-width) solid var(--border)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <h2
          style={{
            margin: '0 0 var(--space-5)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-h2)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--text-heading)',
          }}
        >
          Budynki wg etapu
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          {STATE_ORDER.map((state) => {
            const meta = LIFECYCLE_META[state];
            const value = stageCount(stageCounts, state);
            const interactive = !!onNavigateToStage;
            const activate = interactive ? () => onNavigateToStage!(state) : undefined;
            return (
              <div
                key={state}
                data-testid={`dashboard-stage-${state}`}
                onClick={activate}
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                onKeyDown={
                  interactive
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          activate!();
                        }
                      }
                    : undefined
                }
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-4)',
                  background: meta.bg,
                  border: 'var(--border-width) solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  minWidth: 0,
                  cursor: interactive ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-display)',
                    fontWeight: 'var(--weight-bold)',
                    lineHeight: 'var(--leading-tight)',
                    color: 'var(--text-heading)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {value}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-label)',
                    fontWeight: 'var(--weight-semibold)',
                    color: meta.fg,
                  }}
                >
                  {meta.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Quick actions */}
      <section
        style={{
          padding: 'var(--space-5)',
          background: 'var(--changed-bg)',
          border: 'var(--border-width) solid var(--border)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <h2
          style={{
            margin: '0 0 var(--space-2)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-h2)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--text-heading)',
          }}
        >
          Szybkie akcje
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
            color: 'var(--text-secondary)',
          }}
        >
          Użyj menu nawigacji po lewej stronie, aby zarządzać budynkami, przeglądami i lokatorami.
        </p>
      </section>
    </div>
  );
};
